import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Select, { SelectOption } from '@components/common/Select';
import styles from './Pagination.module.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  itemName?: string; // e.g., "contracts", "assessments", "suppliers"
  pageSizeOptions?: number[]; // e.g., [25, 50, 100]
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemName = 'items',
  pageSizeOptions = [25, 50, 100]
}) => {
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const handlePageSizeChange = (value: string) => {
    if (onPageSizeChange) {
      onPageSizeChange(Number(value));
      // Reset to page 1 when changing page size
      onPageChange(1);
    }
  };

  // Convert pageSizeOptions to SelectOption format
  const selectOptions: SelectOption[] = pageSizeOptions.map((option) => ({
    value: option.toString(),
    label: option.toString()
  }));

  // Don't render pagination if there are no items
  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={styles.tablePagination}>
      <div className={styles.tablePaginationInfo}>
        <span>
          Showing {startIndex + 1}-{endIndex} of {totalItems} {itemName}
        </span>

        {onPageSizeChange && (
          <div className={styles.tablePaginationSize}>
            <label htmlFor="pageSize">Show:</label>
            <Select
              options={selectOptions}
              value={pageSize.toString()}
              onChange={handlePageSizeChange}
              className={styles.tablePaginationSelect}
            />
          </div>
        )}
      </div>

      <div className={styles.tablePaginationControls}>
        <button
          className={styles.tablePaginationBtn}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
        </button>

        <span className={styles.tablePaginationCurrent}>
          Page {currentPage} of {totalPages}
        </span>

        <button
          className={styles.tablePaginationBtn}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
